import React, { useCallback, useMemo, useRef, useState } from "react";
import { useGetCommunicationItemsInfiniteQuery, useGetContactActivitySummaryQuery } from "@/store/rest_api";
import CommunicationItem from "@/model/interface/CommunicationItem";
import { Skeleton } from "@/components/ui/skeleton";
import ActivityItem from "./ActivityItem";
import { ActivityEntry } from "@/model/interface/Activity";
import { formatDate } from "@/util/call";
import { ChevronRight, Loader2, Phone, MessageSquare } from "lucide-react";
import { format, parseISO } from "date-fns";

interface ActivityTimelineProps {
  contactId: string;
  compact?: boolean;
}

const FULL_PAGE_SIZE = 50;
const DAY_PAGE_SIZE = 100;
const LOAD_AHEAD_MARGIN = "200px";

function toEntries(items: CommunicationItem[]): ActivityEntry[] {
  const entries: ActivityEntry[] = [];
  items.forEach((item) => {
    if (item.kind === "TEXT") {
      if (item.message) {
        entries.push({ type: "MESSAGE", timestamp: item.occurredAt, message: item.message, conversationId: item.conversationId });
      }
      return;
    }
    if (item.kind === "FLAG") {
      if (item.preview) {
        entries.push({
          type: "FLAG",
          timestamp: item.occurredAt,
          preview: item.preview,
          phoneNumber: item.phoneNumber,
        });
      }
      return;
    }
    if (item.kind === "NOTE") {
      if (item.note) {
        entries.push({ type: "NOTE", timestamp: item.occurredAt, note: item.note });
      }
      return;
    }
    if (item.call) {
      entries.push({ type: "CALL", timestamp: item.occurredAt, call: item.call });
    }
  });
  return entries;
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ contactId, compact = false }) => {
  if (compact) {
    return <CompactTimeline contactId={contactId} />;
  }
  return <FullTimeline contactId={contactId} />;
};

function FullTimeline({ contactId }: { contactId: string }) {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useGetCommunicationItemsInfiniteQuery({
    contactId,
    includeNotes: true,
    size: FULL_PAGE_SIZE,
  });

  const grouped = useMemo(() => {
    const entries = toEntries(data?.pages.flatMap((page) => page.items) ?? []);
    const groups = new Map<string, ActivityEntry[]>();
    entries.forEach((entry) => {
      const dateKey = formatDate(new Date(entry.timestamp));
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(entry);
    });
    return groups;
  }, [data]);

  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (!node || !hasNextPage || isFetchingNextPage) {
        return;
      }
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            void fetchNextPage();
          }
        },
        { rootMargin: LOAD_AHEAD_MARGIN },
      );
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (grouped.size === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        No activity yet
      </div>
    );
  }

  return (
    <div>
      {Array.from(grouped.entries()).map(([date, entries]) => (
        <div key={date}>
          <div className="sticky top-0 bg-muted px-4 py-2 text-sm font-medium text-muted-foreground">
            {date}
          </div>
          <div className="divide-y divide-border">
            {entries.map((entry, index) => (
              <ActivityItem key={`${entry.type}-${entry.timestamp}-${index}`} entry={entry} />
            ))}
          </div>
        </div>
      ))}
      {hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center px-4 py-3">
          {isFetchingNextPage && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        </div>
      )}
    </div>
  );
}

function CompactTimeline({ contactId }: { contactId: string }) {
  const { data: summary, isLoading } = useGetContactActivitySummaryQuery(contactId);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => {
    const today = new Date().toISOString().slice(0, 10);
    return new Set([today]);
  });

  const toggleDay = (date: string) => {
    setExpandedDays((previous) => {
      const next = new Set(previous);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (!summary || summary.days.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        No activity yet
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 py-3 border-b border-border space-y-1">
        {summary.contactSince && (
          <p className="text-xs text-muted-foreground">
            Contact since {format(new Date(summary.contactSince), "MMM d, yyyy")}
          </p>
        )}
        <div className="flex items-center gap-3 text-sm">
          <span className="flex items-center gap-1">
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">{summary.totalCalls}</span>
            <span className="text-muted-foreground">calls</span>
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">{summary.totalMessages}</span>
            <span className="text-muted-foreground">messages</span>
          </span>
        </div>
      </div>

      {summary.days.map((day) => {
        const isExpanded = expandedDays.has(day.date);
        const displayDate = formatDate(parseISO(day.date));

        return (
          <div key={day.date}>
            <button
              onClick={() => toggleDay(day.date)}
              className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-accent/50 transition-colors text-left border-b border-border"
            >
              <ChevronRight
                className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${isExpanded ? "rotate-90" : ""}`}
              />
              <span className="text-sm font-medium flex-1">{displayDate}</span>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {day.callCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {day.callCount}
                  </span>
                )}
                {day.messageCount > 0 && (
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    {day.messageCount}
                  </span>
                )}
              </div>
            </button>
            {isExpanded && <ExpandedDayItems contactId={contactId} date={day.date} />}
          </div>
        );
      })}
    </div>
  );
}

function ExpandedDayItems({ contactId, date }: { contactId: string; date: string }) {
  const { data, isLoading } = useGetCommunicationItemsInfiniteQuery({
    contactId,
    includeNotes: true,
    from: `${date}T00:00:00Z`,
    to: `${date}T23:59:59.999Z`,
    size: DAY_PAGE_SIZE,
  });

  const entries = useMemo(() => toEntries(data?.pages.flatMap((page) => page.items) ?? []), [data]);

  if (isLoading) {
    return (
      <div className="px-4 py-2">
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="divide-y divide-border bg-accent/20">
      {entries.map((entry, index) => (
        <ActivityItem key={`${entry.type}-${entry.timestamp}-${index}`} entry={entry} />
      ))}
    </div>
  );
}

export default ActivityTimeline;
